import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import { bookingUrl, brand, paths } from '../data/content';
import { AgeRangeCard } from './AgeRangeCard';
import { scrollToSection } from '../utils/scrollToSection';
const lines = [{
  text: 'Find',
  accent: false
}, {
  text: 'your',
  accent: false
}, {
  text: 'edge',
  accent: true
}];
export function Statement() {
  const [activeId, setActiveId] = useState(paths[0].id);
  const active = paths.find(p => p.id === activeId) ?? paths[0];
  return <section id="intro" className="relative flex w-full flex-col justify-between overflow-hidden bg-pearl-50 pb-10 pt-8 dark:bg-slate-950 sm:pt-10 lg:min-h-[88vh]" data-id="element-245" data-mp="1n2sax4m2uxng">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col justify-between px-5 sm:px-8" data-id="element-246" data-mp="1wtn1gl6cwjep">
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.15
      }} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400" data-id="element-247" data-mp="20xba776q5l1j">
          <span className="flex items-center gap-2 text-slate-900 dark:text-pearl-100" data-id="element-248" data-mp="2l6obo396i02c">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-butter-500" data-id="element-249" data-mp="2g9fofo96b5u8" />
            {brand.name}
          </span>
          <span aria-hidden="true" className="hidden text-slate-300 dark:text-slate-700 sm:inline" data-id="element-250" data-mp="2bb4gxk6cp7f6">
            /
          </span>
          <span data-id="element-251" data-mp="33xyxi601rmnd">Early career &amp; early talent coaching</span>
        </motion.p>

        <div className="my-7 grid gap-8 sm:my-10 sm:gap-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-12" data-id="element-252" data-mp="3401vpdbcsm36">
          <div data-id="element-253" data-mp="2u85qliaunvpg">
          <h1 className="select-none" data-id="element-254" data-mp="2u8nu8zzudc7e">
            <span className="sr-only" data-id="element-255" data-mp="2u7mfoljma7kx">
              Find your edge — early career coaching for high schoolers, college students, and new
              grads
            </span>
            <span aria-hidden="true" className="block" data-id="element-256" data-mp="3mvs1zcqbk4mv">
              {lines.map((line, i) => <span key={line.text} className="block overflow-hidden" data-id="element-257" data-mp="1kyrby012glsv" data-mpi={i}>
                  <motion.span initial={{
                  y: '110%'
                }} animate={{
                  y: '0%'
                }} transition={{
                  duration: 0.95,
                  delay: 0.1 + i * 0.12,
                  ease: [0.22, 1, 0.36, 1]
                }} className={`block font-display text-[16vw] font-semibold uppercase leading-[0.84] tracking-tight lg:text-[10.5rem] ${line.accent ? 'text-butter-500 dark:text-butter-400' : 'text-slate-900 dark:text-pearl-50'}`} data-id="element-258" data-mp="1p0uzvo4x9iv1">
                    {line.text}
                  </motion.span>
                </span>)}
            </span>
          </h1>

          <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="mt-6 max-w-3xl font-display text-2xl font-semibold leading-snug tracking-tight text-slate-800 dark:text-pearl-100 sm:text-3xl" style={{
            fontSize: "25px"
          }} data-id="element-259" data-mp="x96szh4wxojl">
            Early career talent coaching. 
          </motion.p>

          

          <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 0.65
          }} className="mt-6" data-id="element-260" data-mp="3alj9btfuv0yd">
            <p id="path-label" className="text-sm font-semibold text-slate-500 dark:text-slate-400" data-id="element-261" data-mp="3kgb830l6btuq">
              Where are you at right now?
            </p>

            <div role="tablist" aria-labelledby="path-label" className="mt-3 flex flex-wrap gap-2" data-id="element-262" data-mp="3tj3zr4y9q7l3">
              {paths.map(path => {
                const selected = path.id === active.id;
                return <button key={path.id} role="tab" type="button" aria-selected={selected} aria-controls="path-panel" onClick={() => setActiveId(path.id)} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 ${selected ? 'border-slate-900 bg-slate-900 text-pearl-50 dark:border-butter-300 dark:bg-butter-300 dark:text-slate-900' : 'border-slate-300 bg-white/70 text-slate-700 hover:border-butter-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'}`} data-id="element-263" data-mp="1z41g3nvwhjq3">
                    {path.label}
                  </button>;
              })}
            </div>

            <div id="path-panel" role="tabpanel" className="mt-4 min-h-[8.5rem] sm:min-h-[8rem]" data-id="element-264" data-mp="1dv6ur480i62i">
              <motion.div key={active.id} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.25,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-265" data-mp="1hwa4ra3dp7vt">
                <p className="max-w-xl leading-relaxed text-slate-600 dark:text-slate-300" data-id="element-266" data-mp="u5d5awsrs31g">
                  {active.blurb}
                </p>
              </motion.div>
            </div>
          </motion.div>
          </div>

          <AgeRangeCard data-id="element-267" data-mp="rj435hsund53" />
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} className="flex flex-col gap-6 border-t border-slate-200 pt-6 dark:border-slate-800 sm:flex-row sm:items-end sm:justify-between" data-id="element-268" data-mp="mm3vj2gcq2bv">
          <p className="max-w-md text-lg leading-relaxed text-slate-600 dark:text-slate-300" data-id="element-269" data-mp="8ofb1vq74x7c">
            Based in Austin, TX. 
Virtual sessions available throughout the U.S.
          </p>

          <div className="flex items-center gap-4" data-id="element-270" data-mp="3v0447yoc2njc">
            <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex flex-1 items-center justify-center rounded-full bg-slate-900 px-6 py-3.5 sm:flex-none text-sm font-semibold text-pearl-50 transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-700 dark:bg-butter-400 dark:text-slate-950 dark:hover:bg-butter-300" data-id="element-271" data-mp="3q3vjm5rda24s">
              Book a free call
            </a>
            <a href="#coaching" onClick={e => scrollToSection(e, '#coaching')} aria-label="Scroll to see how the coaching works" className="group inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-slate-600 transition-colors hover:border-butter-400 hover:text-butter-600 dark:border-slate-700 dark:text-slate-300" data-id="element-272" data-mp="3bgnbjdtcucx1">
              <motion.span animate={{
              y: [0, 4, 0]
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-273" data-mp="3q7nn3rpeemxe">
                <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-274" data-mp="3lel247yoslst" />
              </motion.span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}